import styled, { css, keyframes } from 'styled-components';
import { SizesType, StatusButtonType } from '../ThemeProvider/types';
import { ButtonHierarchy, ButtonIconPosition } from './types';

const spin = keyframes`
from {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  to {
    -webkit-transform: rotate(359deg);
    transform: rotate(359deg);
    -webkit-transform: rotate(359deg);
    transform: rotate(359deg);
  }
`;

const ButtonFlex = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

export const ButtonContainer = styled.div`
  position: relative;
  ${ButtonFlex}
`;

export const ButtonText = styled.p`
  margin: 0;
`;

export const Spinner = styled.div<{
  size: SizesType;
  status: StatusButtonType;
  hierarchy: ButtonHierarchy;
}>`
  display: block;
  border: 2px solid;
  border-radius: 50%;
  -webkit-animation: ${spin} 1s infinite linear;
  animation: ${spin} 1s infinite linear;
  ${({ theme, size, hierarchy, status }) => css`
    width: ${theme.button.sizes[size].spinner}px;
    height: ${theme.button.sizes[size].spinner}px;
    border-color: ${theme.grayCC};
    border-top-color: ${hierarchy === 'primary' ? theme.status[status].contrast : theme.gray4D};
  `}
`;

export const SubmittingContainer = styled.div<{
  loaderWidth: number;
  status: StatusButtonType;
  hasIcon: boolean;
}>`
  position: absolute;
  left: -2px;
  top: -2px;
  height: 100%;
  width: ${({ hasIcon }) => (hasIcon ? 'calc(100% - 4px)' : '100%')};
  border-radius: 50px;
  border-width: 2px;
  border-style: solid;
  ${ButtonFlex}

  ${({ theme, status }) => css`
    border-color: ${theme.status[status].color};
    background-color: ${theme.status[status].color};
    color: ${theme.status[status].contrast};

    & > div svg > path,
    & > div svg > rect,
    & > div svg > circle {
      stroke: ${theme.status[status].contrast} !important;
    }
  `};

  transition: clip-path 800ms linear, background-color 600ms linear;
  clip-path: ${({ loaderWidth }) => `inset(0 ${100 - loaderWidth}% 0 0)`};
`;

export const Button = styled.button<{
  status: StatusButtonType;
  hierarchy: ButtonHierarchy;
  size: SizesType;
  isSubmitting: boolean;
  fullWidth: boolean;
  hasIcon: boolean;
  iconPosition?: ButtonIconPosition;
  circular?: boolean;
  fontWeight?: 'normal' | 'bold';
  isMobile?: boolean;
}>`
  position: relative;
  border-style: solid;
  border-width: 2px;
  font-family: 'Mulish', sans-serif;
  font-weight: ${({ fontWeight }) => fontWeight};
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  -webkit-tap-highlight-color: transparent;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  border-radius: 50px;
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
  padding: 0;
  cursor: ${({ isSubmitting }) => (isSubmitting ? 'auto' : 'pointer')};
  outline: none;

  ${({ theme: { button }, size, isMobile }) => `
    font-size: ${isMobile ? button.mobileFontSize : button.sizes[size].fontSize}px;
    line-height: ${button.sizes[size].lineHeight}px;
    height: ${button.sizes[size].height}px;
  `}

  ${({ theme: { button }, size, hasIcon, iconPosition, circular }) =>
    circular
      ? `
    padding-left: ${button.sizes[size].circularPadding}px;
    padding-right: ${button.sizes[size].circularPadding}px;
  `
      : `padding-left: ${
          hasIcon && iconPosition === 'left'
            ? button.sizes[size].padding - 4
            : button.sizes[size].padding
        }px;
    padding-right: ${
      hasIcon && iconPosition === 'right'
        ? button.sizes[size].padding - 4
        : button.sizes[size].padding
    }px;`}

  ${({ hasIcon, iconPosition }) =>
    hasIcon &&
    (iconPosition === 'left'
      ? css`
          & > ${SubmittingContainer} {
            padding-right: 4px;
          }
        `
      : css`
          & > ${SubmittingContainer} {
            padding-left: 4px;
          }
        `)}

  & > ${ButtonContainer},
  & > ${SubmittingContainer} {
    flex-direction: ${({ iconPosition }) => (iconPosition === 'left' ? 'row' : 'row-reverse')};
  }

  & > ${ButtonContainer} > div {
    cursor: ${({ isSubmitting }) => (isSubmitting ? 'auto' : 'pointer')};
  }

  &:disabled {
    cursor: auto;
    & > ${ButtonContainer} > div {
      cursor: auto;
    }
  }

  & > div svg > path,
  & > div svg > rect,
  & > div svg > circle {
    stroke-width: 2px;
  }

  ${({ status, hierarchy, theme, isSubmitting }) => {
    switch (hierarchy) {
      case 'primary':
        return css`
          background-color: ${theme.status[status].color};
          border-color: ${theme.status[status].color};
          color: ${theme.status[status].contrast};

          & > div svg > path,
          & > div svg > rect,
          & > div svg > circle {
            stroke: ${theme.status[status].contrast} !important;
          }

          ${!isSubmitting &&
          css`
            &:hover {
              background-color: ${theme.status[status].hover};
              border-color: ${theme.status[status].hover};
            }

            &:active {
              background-color: ${theme.status[status].active};
              border-color: ${theme.status[status].active};
            }
          `}

          &:disabled {
            background-color: ${theme.grayCC};
            border-color: ${theme.grayCC};
          }
        `;
      case 'primary-alt':
        return css`
          background-color: ${theme.status[status].contrast};
          border-color: ${theme.status[status].contrast};
          color: ${theme.status[status].color};

          & > div svg > path,
          & > div svg > rect,
          & > div svg > circle {
            stroke: ${theme.status[status].color} !important;
          }

          ${!isSubmitting &&
          css`
            &:hover {
              color: ${theme.status[status].hover};
              & > div svg > path,
              & > div svg > rect,
              & > div svg > circle {
                stroke: ${theme.status[status].hover} !important;
              }
            }

            &:active {
              color: ${theme.status[status].active};
              & > div svg > path,
              & > div svg > rect,
              & > div svg > circle {
                stroke: ${theme.status[status].active} !important;
              }
            }
          `}

          &:disabled {
            background-color: ${theme.grayCC};
            border-color: ${theme.grayCC};
          }
        `;
      case 'secondary':
        return css`
          background-color: transparent;
          border-color: ${theme.status[status].color};
          color: ${theme.gray4D};
          & > div svg > path,
          & > div svg > rect,
          & > div svg > circle {
            stroke: ${theme.gray4D};
          }

          ${!isSubmitting &&
          css`
            &:hover {
              color: ${theme.status[status].hover};
              border-color: ${theme.status[status].hover};

              & > div svg > path,
              & > div svg > rect,
              & > div svg > circle {
                stroke: ${theme.status[status].hover};
              }
            }

            &:active {
              color: ${theme.status[status].active};
              border-color: ${theme.status[status].active};

              & > div svg > path,
              & > div svg > rect,
              & > div svg > circle {
                stroke: ${theme.status[status].active};
              }
            }
          `}

          &:disabled {
            color: ${theme.grayCC};
            border-color: ${theme.grayCC};

            & > div svg > path,
            & > div svg > rect,
            & > div svg > circle {
              stroke: ${theme.grayCC};
            }
          }
        `;
      case 'tertiary':
        return css`
          background-color: transparent;
          border-color: transparent;
          color: ${theme.gray4D};

          & > div svg > path,
          & > div svg > rect,
          & > div svg > circle {
            stroke: ${theme.gray4D};
          }

          ${!isSubmitting &&
          css`
            &:hover {
              color: ${theme.status[status].color};
              background-color: ${theme.status[status].opacity};
              border-color: ${theme.status[status].opacity};

              & > div svg > path,
              & > div svg > rect,
              & > div svg > circle {
                stroke: ${theme.status[status].color};
              }
            }

            &:active {
              color: ${theme.status[status].active};
              background-color: ${theme.status[status].opacity};
              border-color: ${theme.status[status].opacity};

              & > div svg > path,
              & > div svg > rect,
              & > div svg > circle {
                stroke: ${theme.status[status].active};
              }
            }
          `}

          &:disabled {
            background-color: transparent;
            border-color: transparent;
            color: ${theme.grayCC};

            & > div svg > path,
            & > div svg > rect,
            & > div svg > circle {
              stroke: ${theme.grayCC};
            }
          }
        `;
      case 'minimal':
        return css`
          height: fit-content;
          width: fit-content;
          background-color: transparent;
          border: none;
          padding: 0;
          color: ${theme.gray4D};

          & > div svg > path,
          & > div svg > rect,
          & > div svg > circle {
            stroke: ${theme.gray4D};
          }

          ${!isSubmitting &&
          css`
            &:hover {
              color: ${theme.status[status].color};

              & > div svg > path,
              & > div svg > rect,
              & > div svg > circle {
                stroke: ${theme.status[status].color};
              }
            }
          `}

          &:disabled {
            background-color: transparent;
            color: ${theme.grayCC};

            & > div svg > path,
            & > div svg > rect,
            & > div svg > circle {
              stroke: ${theme.grayCC};
            }
          }
        `;
      case 'minimal-alt':
        return css`
          height: fit-content;
          width: fit-content;
          background-color: transparent;
          border: none;
          padding: 0;
          color: ${theme.status[status].color};

          & > div svg > path,
          & > div svg > rect,
          & > div svg > circle {
            stroke: ${theme.status[status].color} !important;
          }

          ${!isSubmitting &&
          css`
            &:hover {
              color: ${theme.status[status].hover};

              & > div svg > path,
              & > div svg > rect,
              & > div svg > circle {
                stroke: ${theme.status[status].hover} !important;
              }
            }
          `}

          &:disabled {
            background-color: transparent;
            color: ${theme.grayCC};

            & > div svg > path,
            & > div svg > rect,
            & > div svg > circle {
              stroke: ${theme.grayCC};
            }
          }
        `;
      case 'link':
        return css`
          height: fit-content;
          width: fit-content;
          background-color: transparent;
          border: none;
          border-radius: 0;
          padding: 0;
          color: ${status === 'default'
            ? theme.button.defaultLinkColor
            : theme.status[status].color};

          & > div svg > path,
          & > div svg > rect,
          & > div svg > circle {
            stroke: ${status === 'default'
              ? theme.button.defaultLinkColor
              : theme.status[status].color} !important;
          }

          ${!isSubmitting &&
          css`
            &:hover {
              border-bottom: 1px solid
                ${status === 'default' ? theme.button.defaultLinkColor : theme.status[status].color} !important;
            }
          `}

          &:disabled {
            background-color: transparent;
            border-color: transparent;
            color: ${theme.grayCC};

            & > div svg > path,
            & > div svg > rect,
            & > div svg > circle {
              stroke: ${theme.grayCC};
            }
          }
        `;
      default:
        return css``;
    }
  }}
`;
